/**
 * Infinite Scroll für Collection Pages
 * 
 * SEO-safe Implementation mit:
 * - History API (replace mode)
 * - Intersection Observer
 * - Section Rendering API
 * - Scroll Position Management
 */

if (!customElements.get('infinite-scroll-container')) {
  class InfiniteScrollContainer extends HTMLElement {
    constructor() {
      super();
      this.currentPage = 1;
      this.isLoading = false;
      this.hasNextPage = false;
      this.nextPageUrl = '';
      this.totalPages = 1;
      this.observer = null;
      this.abortController = null;
      this.soldOutLoaded = false;
    }

    connectedCallback() {
      this.productGrid = this.querySelector('[data-product-grid]');
      this.trigger = this.querySelector('[data-infinite-scroll-trigger]');
      this.loadingIndicator = this.querySelector('[data-infinite-scroll-loading]');
      this.endMessage = this.querySelector('[data-infinite-scroll-end]');
      
      if (!this.productGrid) {
        return;
      }

      // Get total pages from data attribute (set by Liquid)
      this.totalPages = parseInt(this.dataset.totalPages || '1');

      this.init();
    }

    init() {
      // Get initial page from current URL
      const currentUrlParams = new URLSearchParams(window.location.search);
      this.currentPage = parseInt(currentUrlParams.get('page') || '1');

      // Parse initial state from trigger element
      this.updateState();

      // Setup Intersection Observer (only if trigger exists)
      if (this.trigger) {
        this.setupObserver();
      }

      // If there's no next page, load sold-out immediately
      if (!this.hasNextPage) {
        this.loadSoldOutProducts();
      }

      // Handle browser back/forward navigation
      window.addEventListener('popstate', this.handlePopState.bind(this));
    }

    updateState() {
      if (!this.trigger) {
        this.hasNextPage = false;
        this.nextPageUrl = '';
        return;
      }

      this.hasNextPage = this.trigger.dataset.hasNextPage === 'true';
      this.nextPageUrl = this.trigger.dataset.nextPageUrl || '';
    }

    setupObserver() {
      // Disconnect existing observer
      if (this.observer) {
        this.observer.disconnect();
      }

      // Create new Intersection Observer
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.isLoading && this.hasNextPage) {
              this.loadNextPage();
            }
          });
        },
        {
          rootMargin: '200px 0px', // Start loading 200px before trigger is visible
          threshold: 0
        }
      );

      this.observer.observe(this.trigger);
    }

    async loadNextPage() {
      if (this.isLoading || !this.hasNextPage) return;

      this.isLoading = true;
      this.showLoadingState();

      // Create AbortController for fetch timeout
      this.abortController = new AbortController();
      const timeoutId = setTimeout(() => this.abortController.abort(), 10000);

      try {
        // Use Section Rendering API
        const url = new URL(this.nextPageUrl, window.location.origin);
        url.searchParams.set('sections', 'main-collection-product-grid');

        // Extract the page number we're about to load
        const urlParams = new URLSearchParams(this.nextPageUrl.split('?')[1] || '');
        const pageBeingLoaded = parseInt(urlParams.get('page') || '2');

        const response = await fetch(url.toString(), {
          signal: this.abortController.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        const htmlString = data['main-collection-product-grid'];

        if (!htmlString) {
          throw new Error('Section data not found');
        }

        // Parse response and update state
        this.parseAndAppendProducts(htmlString);

        // Set current page to the page we just loaded
        this.currentPage = pageBeingLoaded;

        // Update URL with History API (replace mode for SEO)
        this.updateURL();

        // Re-observe trigger if there's a next page
        if (this.hasNextPage) {
          this.setupObserver();
        } else {
          this.showEndMessage();
          
          // Load sold-out products at the end
          this.loadSoldOutProducts();
        }

      } catch (error) {
        if (error.name !== 'AbortError') {
          this.showError();
        }
      } finally {
        this.isLoading = false;
        this.hideLoadingState();
      }
    }

    parseAndAppendProducts(htmlString) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlString, 'text/html');
      
      // Find product grid items
      const newProductGrid = doc.querySelector('[data-product-grid]');
      if (!newProductGrid) {
        throw new Error('Product grid not found in response');
      }

      const newProducts = newProductGrid.querySelectorAll('[data-product-item]');
      
      if (newProducts.length === 0) {
        throw new Error('No products found in response');
      }

      const existingHandles = new Set(
        Array.from(this.productGrid.querySelectorAll('[data-handle-collection]')).map(
          (el) => el.dataset.handleCollection
        )
      );

      // Append each product with animation
      newProducts.forEach((product, index) => {
        const handleElement = product.querySelector('[data-handle-collection]');
        const handleKey = handleElement?.dataset?.handleCollection;

        if (handleKey && existingHandles.has(handleKey)) {
          return;
        }
        // Set animation delay
        if (product.style) {
          const delay = index * 50; // 50ms stagger
          product.style.setProperty('--animation-delay', `${delay}ms`);
        }

        this.productGrid.appendChild(product);

        if (handleKey) {
          existingHandles.add(handleKey);
        }

        // Trigger animation if scroll animations are enabled
        if (this.productGrid.dataset.cascade !== undefined) {
          requestAnimationFrame(() => {
            product.classList.add('scroll-trigger--visible');
          });
        }
      });

      // Update trigger element data attributes from new page response
      const newTrigger = doc.querySelector('[data-infinite-scroll-trigger]');
      if (newTrigger) {
        const newHasNextPage = newTrigger.dataset.hasNextPage;
        const newNextPageUrl = newTrigger.dataset.nextPageUrl;
        
        // Update trigger attributes
        this.trigger.dataset.hasNextPage = newHasNextPage;
        this.trigger.dataset.nextPageUrl = newNextPageUrl;
        
        // Also update instance variables immediately
        this.hasNextPage = newHasNextPage === 'true';
        this.nextPageUrl = newNextPageUrl || '';
      } else {
        // No trigger found in response = no more pages
        this.hasNextPage = false;
        this.nextPageUrl = '';
      }

      // Fire custom event for other scripts (e.g., quick-add)
      this.dispatchEvent(new CustomEvent('infinite-scroll:products-loaded', {
        bubbles: true,
        detail: { productCount: newProducts.length }
      }));
    }

    updateURL() {
      const url = new URL(window.location);
      
      // Only update URL if we're within actual paginated pages
      // Don't go beyond totalPages to avoid 404s on direct access
      const pageToShow = Math.min(this.currentPage, this.totalPages);
      
      if (pageToShow > 1) {
        url.searchParams.set('page', pageToShow);
      } else {
        url.searchParams.delete('page');
      }

      // Use replaceState to avoid history clutter (Shopify recommendation)
      window.history.replaceState(
        { page: pageToShow },
        '',
        url.toString()
      );
    }

    handlePopState(event) {
      // Handle browser back/forward
      const page = event.state?.page || 1;
      
      if (page < this.currentPage) {
        // User went back - reload page to avoid complexity
        window.location.reload();
      }
    }

    showLoadingState() {
      if (this.loadingIndicator) {
        this.loadingIndicator.style.display = 'flex';
      }
      if (this.trigger) {
        this.trigger.setAttribute('aria-busy', 'true');
      }
    }

    hideLoadingState() {
      if (this.loadingIndicator) {
        this.loadingIndicator.style.display = 'none';
      }
      if (this.trigger) {
        this.trigger.removeAttribute('aria-busy');
      }
    }

    showEndMessage() {
      if (this.trigger) {
        this.trigger.style.display = 'none';
      }
      if (this.endMessage) {
        this.endMessage.style.display = 'flex';
      }
    }

    showError() {
      // Show fallback link to next page
      if (this.trigger) {
        const fallbackLink = this.trigger.querySelector('[data-infinite-scroll-fallback]');
        if (fallbackLink && this.nextPageUrl) {
          fallbackLink.href = this.nextPageUrl;
          fallbackLink.style.display = 'inline-flex';
        }
      }
    }

    async loadSoldOutProducts() {
      if (this.soldOutLoaded) return;
      this.soldOutLoaded = true;
      // Load sold-out products and append them at the end
      // This replicates the existing sold-out logic from the original template
      try {
        const url = `${window.location.origin}${window.location.pathname}?filter.v.availability=0&sections=main-collection-product-grid`;
        
        const response = await fetch(url);
        const data = await response.json();
        const htmlString = data['main-collection-product-grid'];
        
        if (!htmlString) {
          return;
        }
        
        const tempContainer = document.createElement('div');
        tempContainer.innerHTML = htmlString;
        
        const productCards = tempContainer.querySelector('#product-grid');
        if (!productCards) {
          return;
        }
        
        const soldOutItems = productCards.querySelectorAll('li[data-product-item]');
        const targetContainer = this.productGrid;
        const existingItems = new Map();

        Array.from(targetContainer.querySelectorAll('li[data-product-item]')).forEach((item) => {
          const handleElement = item.querySelector('[data-handle-collection]');
          const handleKey = handleElement?.dataset?.handleCollection;

          if (handleKey) {
            existingItems.set(handleKey, item);
          }
        });
        
        soldOutItems.forEach(card => {
          const handleElement = card.querySelector('[data-handle-collection]');
          const handleKey = handleElement?.dataset?.handleCollection;

          if (handleKey && existingItems.has(handleKey)) {
            const existingItem = existingItems.get(handleKey);
            existingItem?.removeAttribute('hidden');
            existingItem?.style?.removeProperty('display');
            targetContainer.appendChild(existingItem);
            return;
          }

          // Remove hidden attribute to make them visible
          card.removeAttribute('hidden');
          card.style.removeProperty('display');
          targetContainer.appendChild(card);

          if (handleKey) {
            existingItems.set(handleKey, card);
          }
        });
        
        // Fire event for sold-out products loaded
        this.dispatchEvent(new CustomEvent('infinite-scroll:soldout-loaded', {
          bubbles: true,
          detail: { productCount: soldOutItems.length }
        }));
        
      } catch (error) {
        // Silent fail - sold-out products are a nice-to-have
      }
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
      }
      if (this.abortController) {
        this.abortController.abort();
      }
      window.removeEventListener('popstate', this.handlePopState);
    }
  }

  customElements.define('infinite-scroll-container', InfiniteScrollContainer);
}

